import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasTitleText, readPresentation, titlePlaceholder } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionAufbau: Lesson = {
  id: "aufbau",
  title: "Wie eine Präsentation aufgebaut ist",
  summary: "Folie, Layout, Folienmaster, Design – vier Ebenen, die aufeinander aufbauen.",
  durationMinutes: 9,
  learningGoals: [
    "Die vier Ebenen Folie, Layout, Folienmaster und Design unterscheiden",
    "Eine Präsentation aus mehreren Folien anlegen",
    "Die Titelfolie über ihren Platzhalter füllen",
  ],
  steps: [
    {
      id: "vier-ebenen",
      title: "Vier Ebenen, nicht eine",
      explanation: [
        "Wer PowerPoint zum ersten Mal öffnet, sieht eine weiße Fläche und denkt: eine " +
          "Leinwand. Genau dieses Missverständnis kostet später die meiste Zeit. " +
          "PowerPoint ist keine Leinwand, sondern ein Baukasten aus vier Ebenen, die " +
          "übereinander liegen.",
        "Ganz unten steht das Design. Es legt Farben, Schriften und Effekte für die " +
          "gesamte Präsentation fest. Darüber liegt der Folienmaster: Er bestimmt, wo " +
          "Titel, Inhalt, Seitenzahl und Fußzeile sitzen und wie groß die Schrift ist. " +
          "Zum Master gehören mehrere Layouts – eines für die Titelfolie, eines für " +
          "Titel und Inhalt, eines für zwei Spalten und so weiter.",
        "Erst ganz oben steht die einzelne Folie. Sie erbt ihr Aussehen vom Layout, das " +
          "Layout vom Master, der Master vom Design. Wenn du eine Folie über ihr Layout " +
          "aufbaust, wirkt sich später jede Änderung am Design automatisch auf alle " +
          "Folien aus. Ziehst du stattdessen freie Textfelder auf die leere Fläche, " +
          "hängt diese Folie in der Luft – und muss bei jeder Designänderung von Hand " +
          "nachgezogen werden.",
        "Das ist der rote Faden dieses Kurses und die genaue Entsprechung zu den " +
          "Formatvorlagen im Word-Kurs: Nicht das Aussehen einer Folie zählt, sondern " +
          "ihre Struktur.",
      ],
      keyPoints: [
        "Design → Folienmaster → Layout → Folie: jede Ebene erbt von der darunter.",
        "Ansicht → Folienmaster zeigt Master und Layouts zum Bearbeiten.",
        "Freie Textfelder erben nichts – sie sind der häufigste Grund für Nacharbeit.",
      ],
    },
    {
      id: "folien-anlegen",
      title: "Folien anlegen",
      explanation: [
        "Eine neue Folie entsteht über „Start“ → „Neue Folie“. Wichtig ist der kleine " +
          "Pfeil unter der Schaltfläche: Er öffnet den Layoutkatalog. Klickst du nur auf " +
          "die Schaltfläche, übernimmt PowerPoint das Layout der aktuellen Folie – was " +
          "meistens richtig, manchmal aber überraschend ist.",
        "Zum Einstieg reicht eine Handvoll Folien. Sinnvoll ist von Anfang an eine " +
          "Dreiteilung, die sich in fast jeder Präsentation wiederfindet: eine " +
          "Titelfolie, mindestens eine Inhaltsfolie und eine Schlussfolie.",
        "Anders als in Word gibt es keine automatisch fortlaufende Seite. Jede Folie ist " +
          "ein eigenes Objekt, das du anlegst, verschiebst, duplizierst oder löschst.",
      ],
      keyPoints: [
        "Strg + M legt eine neue Folie an.",
        "Der Pfeil unter „Neue Folie“ öffnet den Layoutkatalog.",
        "Strg + D dupliziert die markierte Folie samt Inhalt.",
      ],
      task: {
        instruction:
          "Lege eine Präsentation mit mindestens drei Folien an: Titelfolie, eine " +
          "Inhaltsfolie und eine Schlussfolie.",
        hint: "Strg + M legt eine neue Folie an – zweimal drücken genügt.",
        check: async () => {
          const snapshot = await readPresentation();
          const anzahl = snapshot.slides.length;

          if (anzahl >= 3) {
            return pass(`${anzahl} Folien gefunden – das Grundgerüst steht.`);
          }
          return fail(
            anzahl === 0
              ? "In der Präsentation ist noch keine Folie vorhanden."
              : `Es ${anzahl === 1 ? "ist erst 1 Folie" : `sind erst ${anzahl} Folien`} vorhanden, gebraucht werden 3.`,
            "Mit Strg + M kommt jeweils eine weitere Folie dazu.",
          );
        },
      },
    },
    {
      id: "titelfolie",
      title: "Die Titelfolie füllen",
      explanation: [
        "Die erste Folie ist die Titelfolie. Sie hat ein eigenes Layout mit zwei " +
          "Platzhaltern: einem großen für den Titel und einem kleineren darunter für den " +
          "Untertitel.",
        "Der Titel ist mehr als eine Überschrift. PowerPoint nutzt ihn in der " +
          "Gliederungsansicht, beim Wechsel zwischen Folien, in der Foliensortierung und " +
          "beim Export – überall dort, wo eine Folie mit einem Namen benannt werden muss. " +
          "Eine Folie ohne Titel taucht an all diesen Stellen als leere Zeile auf.",
        "Ein guter Titel ist eine Aussage, keine Überschrift: „Umsatz wächst im dritten " +
          "Quartal um 12 %“ sagt mehr als „Umsatzzahlen“. Wer die Folien später " +
          "durchblättert, versteht die Botschaft dann allein aus den Titeln.",
      ],
      keyPoints: [
        "Klick in den Platzhalter genügt – nicht in ein neues Textfeld schreiben.",
        "Der Titel ist der Name der Folie in allen Ansichten und im Export.",
        "Aussage statt Kategorie: „Umsatz wächst um 12 %“ statt „Umsatz“.",
      ],
      task: {
        instruction: "Schreibe einen Titel in den Titelplatzhalter deiner ersten Folie.",
        hint: "Direkt in das Feld „Titel durch Klicken hinzufügen“ tippen, kein eigenes Textfeld aufziehen.",
        check: async () => {
          const snapshot = await readPresentation();
          const erste = snapshot.slides[0];

          if (!erste) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst eine Folie an (Strg + M).",
            );
          }
          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Die Platzhalter einer Folie",
              "1.3",
              "Klick in das Feld „Titel durch Klicken hinzufügen“ und prüfe selbst, ob dein Titel dort steht.",
            );
          }

          const titel = titlePlaceholder(erste);
          return fromCriteria(
            [
              criterion("Folie 1 hat einen Titelplatzhalter", titel !== null),
              criterion(
                titel && titel.paragraphs.length > 0
                  ? `Der Titel ist gefüllt: „${titel.paragraphs[0]}“`
                  : "Im Titelplatzhalter steht Text",
                hasTitleText(erste),
              ),
            ],
            {
              success: "Die Titelfolie trägt jetzt einen echten Titel.",
              failure: "Der Titel der ersten Folie fehlt noch.",
              hint:
                titel === null
                  ? "Auf Folie 1 gibt es keinen Titelplatzhalter. Weise ihr über „Start“ → „Layout“ das Layout „Titelfolie“ zu."
                  : "Der Platzhalter ist da, aber leer. Klick hinein und tippe den Titel direkt dort ein.",
            },
          );
        },
      },
    },
  ],
};
